// src/lib/requests.ts
// FLUJO DE SOLICITUDES (solicitud → aprobación/rechazo → polling → notificación).
//
// Modelo:
//   1. El negocio crea una solicitud (ej. restaurar datos, exportar, etc.).
//      Se guarda en local (Dexie) y SUBE a Supabase (tabla support_requests).
//   2. NIIZ (desarrollador) la ve en su panel, aprueba o rechaza → se actualiza en Supabase.
//   3. El negocio hace POLLING de Supabase. Al detectar el cambio de estado,
//      notifica al usuario y restaura SOLO lo correspondiente a esa solicitud.
//      (No toca trabajadores, clientes ni estado de productos).

import { db, type SupportRequest } from '@/db/db';
import { supabase } from '@/lib/supabase';

/** Crea una solicitud PENDING (local + Supabase). */
export const createRequest = async (data: {
  business_id: string;
  business_name: string;
  subject: string;
  message: string;
  request_type?: string;
}): Promise<SupportRequest> => {
  const req: SupportRequest = {
    id: crypto.randomUUID(),
    business_id: data.business_id,
    business_name: data.business_name,
    subject: data.subject,
    message: data.message,
    request_type: data.request_type || 'GENERAL',
    status: 'PENDING',
    created_at: new Date().toISOString(),
    seen_by_business: 0,
  };
  await db.support_requests.add(req);

  if (supabase) {
    try {
      await (supabase as any).from('support_requests').upsert(req as any);
    } catch (e) {
      console.warn('No se pudo subir la solicitud a Supabase:', e);
    }
  }
  return req;
};

/** Lee TODAS las solicitudes (para el panel central del desarrollador) desde Supabase. */
export const getAllRequestsCloud = async (): Promise<any[]> => {
  if (!supabase) return [];
  const { data, error } = await (supabase as any)
    .from('support_requests')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) {
    console.error('Error leyendo solicitudes:', error);
    return [];
  }
  return (data || []) as any[];
};

/** APRUEBA una solicitud desde el panel central (Supabase). */
export const approveRequestCloud = async (requestId: string): Promise<void> => {
  if (!supabase) return;
  await (supabase as any)
    .from('support_requests')
    .update({ status: 'APPROVED', resolved_at: new Date().toISOString() })
    .eq('id', requestId);
};

/** RECHAZA una solicitud desde el panel central (Supabase). */
export const rejectRequestCloud = async (requestId: string): Promise<void> => {
  if (!supabase) return;
  await (supabase as any)
    .from('support_requests')
    .update({ status: 'REJECTED', resolved_at: new Date().toISOString() })
    .eq('id', requestId);
};

/**
 * POLLING del negocio: consulta Supabase por el estado de sus solicitudes.
 * Devuelve las solicitudes que cambiaron de estado (para notificar).
 */
export const pollMyRequests = async (businessId: string): Promise<SupportRequest[]> => {
  if (!supabase) return [];
  const { data, error } = await (supabase as any)
    .from('support_requests')
    .select('*')
    .eq('business_id', businessId)
    .in('status', ['APPROVED', 'REJECTED'])
    .eq('seen_by_business', 0);
  if (error) {
    console.error('Error en polling de solicitudes:', error);
    return [];
  }
  return (data || []) as SupportRequest[];
};

/** Marca una solicitud como "vista" por el negocio (para no re-notificar). */
export const markRequestSeen = async (requestId: string): Promise<void> => {
  await db.support_requests.update(requestId, { seen_by_business: 1 });
  if (supabase) {
    await (supabase as any).from('support_requests').update({ seen_by_business: 1 }).eq('id', requestId);
  }
};
